import { VatCategory } from 'apis/vat-categories/types';
import { initialVatFieldValue } from 'modules/menu/components/VatCategories';
import { objectId } from 'modules/menu/options/utils';
import { LocalisedValue } from 'modules/shared-menu/types';

import { CreateOptionPayload, FormOptionValue, OptionFormData, UpdateOptionPayload } from './types';
import { OptionType, OptionValueAttrs, OptionValue, Option } from '../../types';
import { updatePrimaryLanguageTranslation } from '../../utils';

export const formOptionValueToOptionValueAttrs =
  (primaryLanguage: Language, type: OptionType) =>
  (val: FormOptionValue): OptionValueAttrs => {
    const multiChoiceConfig =
      type === 'multi_choice'
        ? {
            selection_range: {
              min: val.minimumSelections,
              max: val.maximumSelections || 0,
            },
          }
        : undefined;

    return {
      additives: val.additives ? [{ lang: primaryLanguage, value: val.additives, verified: true }] : undefined,
      allergens: val.allergens ? [{ lang: primaryLanguage, value: val.allergens, verified: true }] : undefined,
      deposit_permutation_id: val.depositPermutationId ? val.depositPermutationId : undefined,
      dietary_preferences: val.dietaryPreferences || undefined,
      distributor_information: val.distributorInformation
        ? [{ lang: primaryLanguage, value: val.distributorInformation, verified: true }]
        : undefined,
      enabled: {
        enabled: !val.disabled,
        after: undefined,
        before: undefined,
      },
      external_id: val.externalId || undefined,
      ingredients: val.ingredients ? [{ lang: primaryLanguage, value: val.ingredients, verified: true }] : undefined,
      mandatory_warnings: val.mandatoryWarnings,
      multi_choice_config: multiChoiceConfig,
      name: [{ lang: primaryLanguage, value: val.name, verified: true }],
      nutrition_facts: val.nutritionFacts ? [{ lang: primaryLanguage, value: val.nutritionFacts, verified: true }] : [],
      price: Number(val.price),
      producer_information: val.producerInformation
        ? [{ lang: primaryLanguage, value: val.producerInformation, verified: true }]
        : [],
      visible_to_courier: val.visibleToCourier,
      visible_to_merchant_app: type === 'choice' ? !val.hideWhenSelected : true,
      vat_category_code: (val.vat?.type === 'categories' && val.vat.categoryCode) || undefined,
      vat_percentage: val.vat ? val.vat.percentage : undefined,
    };
  };

export const buildFormOptionValue = (
  primaryLanguage: Language,
  optionValue: OptionValue,
  vatCategories?: VatCategory[],
): FormOptionValue => {
  const nameTranslation = optionValue.name.find(t => t.lang === primaryLanguage);
  return {
    depositPermutationId: optionValue.deposit_permutation_id || '',
    dietaryPreferences: optionValue.dietary_preferences ?? [],
    disabled: !optionValue.enabled.enabled,
    externalId: optionValue.external_id || '',
    hideWhenSelected: !optionValue.visible_to_merchant_app,
    id: optionValue.id,
    mandatoryWarnings: optionValue.mandatory_warnings ?? [],
    maximumSelections:
      !optionValue.multi_choice_config || optionValue.multi_choice_config.selection_range.max === 0
        ? undefined // TODO: These shouldn't be visible in the form when the option is not multi choice
        : optionValue.multi_choice_config.selection_range.max,
    minimumSelections: optionValue.multi_choice_config ? optionValue.multi_choice_config.selection_range.min : 0, // TODO: These shouldn't be visible in the form when the option is not multi choice
    name: nameTranslation ? nameTranslation.value : '',
    price: optionValue.price,
    visibleToCourier: optionValue.visible_to_courier,
    ingredients: optionValue.ingredients?.find(t => t.lang === primaryLanguage)?.value || '',
    additives: optionValue.additives?.find(t => t.lang === primaryLanguage)?.value || '',
    nutritionFacts: optionValue.nutrition_facts?.find(t => t.lang === primaryLanguage)?.value || '',
    allergens: optionValue.allergens?.find(t => t.lang === primaryLanguage)?.value || '',
    producerInformation: optionValue.producer_information?.find(t => t.lang === primaryLanguage)?.value || '',
    distributorInformation: optionValue.distributor_information?.find(t => t.lang === primaryLanguage)?.value || '',
    vat: initialVatFieldValue(
      vatCategories,
      optionValue.vat_category_code || undefined,
      optionValue.vat_percentage ? Number(optionValue.vat_percentage) : undefined,
    ),
  };
};

export const buildNewFormOptionValue = (overrides?: Partial<FormOptionValue>): FormOptionValue => ({
  additives: '',
  allergens: '',
  depositPermutationId: '',
  dietaryPreferences: [],
  disabled: false,
  distributorInformation: '',
  externalId: '',
  hideWhenSelected: false,
  id: objectId(),
  ingredients: '',
  mandatoryWarnings: [],
  maximumSelections: undefined,
  minimumSelections: 0,
  name: '',
  nutritionFacts: '',
  price: 0,
  producerInformation: '',
  visibleToCourier: false,
  vat: { type: 'categories', percentage: undefined, categoryCode: undefined },
  ...overrides,
});

export const buildOptionBoolValuePlaceholders = (): FormOptionValue[] => [
  buildNewFormOptionValue({ price: 0 }),
  buildNewFormOptionValue({ price: 1 }),
];

export const buildCreateOptionFormData = (primaryLanguage: Language, fromOption?: Option): OptionFormData => {
  const getNamePrimaryLanguageValue = (name: LocalisedValue[]) => {
    const nameTranslation = name.find(t => t.lang === primaryLanguage);
    return nameTranslation ? nameTranslation.value : '';
  };

  const buildFormOptionValueCopy = (optionValue: OptionValue): FormOptionValue => ({
    ...buildFormOptionValue(primaryLanguage, optionValue),
    id: objectId(),
  });

  return fromOption
    ? {
        name: getNamePrimaryLanguageValue(fromOption.name),
        type: fromOption.type,
        defaultValueIndex: fromOption.default_value_index,
        values:
          fromOption.type === 'bool' ? [buildNewFormOptionValue()] : fromOption.values.map(buildFormOptionValueCopy),
        boolValues:
          fromOption.type === 'bool'
            ? fromOption.values.map(buildFormOptionValueCopy)
            : buildOptionBoolValuePlaceholders(),
        externalId: fromOption.external_id || '',
        visibleToCourier: fromOption.values.some(value => value.visible_to_courier),
      }
    : {
        name: '',
        values: [buildNewFormOptionValue()],
        type: 'choice',
        boolValues: buildOptionBoolValuePlaceholders(),
        defaultValueIndex: 0,
        externalId: '',
        visibleToCourier: false,
      };
};

export const buildUpdateOptionFormData = (primaryLanguage: Language, option: Option): OptionFormData => {
  const nameTranslation = option.name.find(t => t.lang === primaryLanguage);

  return {
    name: nameTranslation ? nameTranslation.value : '',
    type: option.type,
    defaultValueIndex: option.default_value_index,
    values:
      option.type === 'bool'
        ? [buildNewFormOptionValue()]
        : option.values.map(optionValue => buildFormOptionValue(primaryLanguage, optionValue)),
    boolValues:
      option.type === 'bool'
        ? option.values.map(optionValue => buildFormOptionValue(primaryLanguage, optionValue))
        : buildOptionBoolValuePlaceholders(),
    externalId: option.external_id || '',
    visibleToCourier: option.values.some(value => value.visible_to_courier),
  };
};

export const formValuesToCreateOptionPayload = (
  primaryLanguage: Language,
  formData: OptionFormData,
): CreateOptionPayload => {
  const toOptionValueAttrs = formOptionValueToOptionValueAttrs(primaryLanguage, formData.type);
  return {
    type: formData.type,
    name: formData.name ? [{ lang: primaryLanguage, value: formData.name, verified: true }] : [],
    external_id: formData.externalId || undefined,
    default_value_index: formData.defaultValueIndex,
    values: (formData.type === 'bool' ? formData.boolValues : formData.values).map(value => {
      return {
        ...toOptionValueAttrs(value),
        visible_to_courier: formData.visibleToCourier,
      };
    }),
  };
};

export const formValuesToUpdateOptionPayload = (
  primaryLanguage: Language,
  option: Option,
  formData: OptionFormData,
): UpdateOptionPayload => {
  const toOptionValueAttrs = formOptionValueToOptionValueAttrs(primaryLanguage, formData.type);

  return {
    type: formData.type,
    name: updatePrimaryLanguageTranslation(primaryLanguage, formData.name, option.name),
    external_id: formData.externalId || undefined,
    default_value_index: formData.defaultValueIndex,
    values: (formData.type === 'bool' ? formData.boolValues : formData.values).map(formOptionValue => {
      const attrs = { ...toOptionValueAttrs(formOptionValue), visible_to_courier: formData.visibleToCourier };
      const existingOptionValue = option.values.find(v => v.id === formOptionValue.id);
      if (existingOptionValue !== undefined) {
        return {
          ...attrs,
          name: updatePrimaryLanguageTranslation(primaryLanguage, formOptionValue.name, existingOptionValue.name),
          additives: formOptionValue.additives
            ? updatePrimaryLanguageTranslation(
                primaryLanguage,
                formOptionValue.additives,
                existingOptionValue.additives,
              )
            : undefined,
          allergens: formOptionValue.allergens
            ? updatePrimaryLanguageTranslation(
                primaryLanguage,
                formOptionValue.allergens,
                existingOptionValue.allergens,
              )
            : undefined,
          distributor_information: formOptionValue.distributorInformation
            ? updatePrimaryLanguageTranslation(
                primaryLanguage,
                formOptionValue.distributorInformation,
                existingOptionValue.distributor_information,
              )
            : undefined,
          ingredients: formOptionValue.ingredients
            ? updatePrimaryLanguageTranslation(
                primaryLanguage,
                formOptionValue.ingredients,
                existingOptionValue.ingredients,
              )
            : undefined,
          nutrition_facts: formOptionValue.nutritionFacts
            ? updatePrimaryLanguageTranslation(
                primaryLanguage,
                formOptionValue.nutritionFacts,
                existingOptionValue.nutrition_facts,
              )
            : undefined,
          producer_information: formOptionValue.producerInformation
            ? updatePrimaryLanguageTranslation(
                primaryLanguage,
                formOptionValue.producerInformation,
                existingOptionValue.producer_information,
              )
            : undefined,
          id: existingOptionValue.id,
        };
      }
      return attrs;
    }),
  };
};
